"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useSession, syncSession, getAccessToken, skipSync } from "../lib/auth-client";
import { FEATURE_FLAGS } from "../lib/config";

// Pages that don't require onboarding to be complete
// Pages that don't require onboarding to be complete
const PUBLIC_PATHS = ["/onboard", "/login", "/terms", "/privacy", "/leaderboard", "/"];
const AUTH_ONLY_PATHS = ["/login", "/wallet", "/profile", "/onboard"];

const BACKEND_URL = process.env.NEXT_PUBLIC_API_URL || "https://drop-backend-65296643664.us-central1.run.app";

/**
 * Runs globally to:
 * 1. Sync Better-Auth session → JWT access token
 * 2. After sync, check onboardingComplete — redirect to /onboard if not done
 */
export function AuthSessionBridge() {
    const { data: session, isPending } = useSession();
    const [isSyncing, setIsSyncing] = useState(false);
    const pathname = usePathname();
    const router = useRouter();

    useEffect(() => {
        if (!FEATURE_FLAGS.ENABLE_AUTH) {
            if (AUTH_ONLY_PATHS.some(p => pathname === p || pathname.startsWith(p + "/"))) {
                console.log(`[AuthBridge] Auth disabled - redirecting from ${pathname} to /`);
                router.replace("/");
            }
            skipSync();
            return;
        }

        if (isPending) return;

        if (session?.user && !getAccessToken() && !isSyncing) {
            console.log("[AuthBridge] Syncing JWT session...");
            setIsSyncing(true);
            syncSession()
                .then(async (user) => {
                    if (user) {
                        console.log("[AuthBridge] Session synced successfully.");
                        // Check onboarding status — skip if already on a public/exempt path
                        if (PUBLIC_PATHS.some(p => pathname === p || pathname.startsWith(p + "/"))) return;
                        try {
                            const token = getAccessToken();
                            const res = await fetch(`${BACKEND_URL}/api/v1/auth/me`, {
                                headers: token ? { Authorization: `Bearer ${token}` } : {}
                            });
                            if (res.ok) {
                                const { user: dbUser } = await res.json();
                                const isGuest = dbUser.role === 'guest' || (dbUser.email?.startsWith('guest_') && dbUser.email?.endsWith('@obvio.app'));

                                if (!dbUser.onboardingComplete && !isGuest) {
                                    console.log("[AuthBridge] Onboarding incomplete — redirecting to /onboard");
                                    router.replace("/onboard");
                                }
                            }
                        } catch (err) {
                            console.error("[AuthBridge] Onboarding check failed:", err);
                        }
                    }
                })
                .catch((err) => {
                    console.error("[AuthBridge] Session sync failed:", err);
                })
                .finally(() => {
                    setIsSyncing(false);
                });
        } else if (!session?.user) {
            // No session — trigger ready so page loaders can resolve
            skipSync();
        }
    }, [session, isPending, isSyncing, pathname]);

    return null;
}
